import { phoneTail } from '@delivery/shared';

/**
 * 司机端任务列表的搜索。
 *
 * ★ 为什么是**本地**过滤,不发请求:
 *
 *   司机用搜索的时候通常站在楼下 —— 手里一堆单,要确认「刚才那个姓王的
 *   是不是这一单」。那一刻网络最不可靠(电梯口、地下车库),而他要的那条
 *   数据**已经在手机里**了:任务列表一次就全取回来。
 *   发请求等于把一个本来零延迟、断网也能用的动作,变成一个会转圈、会失败的动作。
 *
 * ★ 搜索**不分栏**。
 *
 *   原来每一栏自己过滤的话会有这种事:他在「配送中」里搜「王」,
 *   而那一单还停在「待取货」—— 屏幕上是「没找到」,
 *   而他明明知道这单在。于是他会以为单丢了。
 *   所以一打字就按**手上全部的单**搜,结果里每张卡片本来就写着状态。
 */

/** 只留数字 —— 电话里的空格、横杠、括号、+ 号都是写法,不是内容 */
function digitsOf(v: string): string {
  return v.replace(/\D+/g, '');
}

/**
 * 两串号码算不算同一个。
 *
 * ★ 同一个号码在新西兰至少四种写法:`+64211234567`、`64211234567`、
 *   `0211234567`、`021 123 4567`。不同的全在**前缀**上 ——
 *   所以只比**后八位**(`phoneTail`),前缀的差别自然就没了:
 *   国家码 `64`、国内前导零 `0` 都落在被截掉的那一段里。
 *
 *   (我先写了「再剥一次前导零」,然后发现它一行行为都不改 ——
 *    截到后八位之后那个 0 早就不在了。所以没留。)
 *
 * ★ 不调 `normalizeLoosePhone`:它要求剥出至少 7 位才算一个号码,
 *   而司机常常只记得后四位。
 *
 * ★ 至少三位才比。两位数字("12")能撞上几乎每一个号码,
 *   搜出来一屏等于没搜。
 */
function phoneHit(stored: string, typed: string): boolean {
  const a = phoneTail(digitsOf(stored));
  const b = digitsOf(typed);
  if (b.length < 3 || a.length === 0) return false;
  return a.endsWith(phoneTail(b));
}

/** 搜索时会被翻到的那几样 */
export interface SearchableTask {
  orderNo: string;
  receiverName: string;
  receiverPhone: string;
  receiverAddress: string;
  receiverNote?: string | null;
  driverNote?: string | null;
  customer?: { name?: string | null } | null;
}

/**
 * 这一单中不中关键词。
 *
 * @param keyword 司机打进去的原话。空白串一律不中(由调用方决定空串时不过滤)
 */
export function taskMatches(task: SearchableTask, keyword: string): boolean {
  const q = keyword.trim().toLowerCase();
  if (q === '') return false;

  /*
   * ★ 电话单独一条路,不能只靠下面那个 includes。
   *   他输 `021 1234`,库里存的是 `+64211234567` ——
   *   直接 includes 的话一个都搜不到,而「搜自己手上的单却搜不到」
   *   会让他下次干脆不用这个框。
   */
  if (phoneHit(task.receiverPhone, q)) return true;

  const haystack = [
    task.orderNo,
    task.receiverName,
    task.receiverAddress,
    task.receiverNote ?? '',
    task.driverNote ?? '',
    task.customer?.name ?? '',
  ]
    .join('\n')
    .toLowerCase();

  /*
   * ★ 空格当「并且」。
   *
   *   他打「王 皇后街」想要的是「姓王的那单,在皇后街」——
   *   当成一整串去 includes 的话必然零结果,
   *   而零结果给不出任何下一步该怎么做的提示。
   */
  return q.split(/\s+/).every((word) => haystack.includes(word));
}

/** 按关键词筛一遍。关键词是空白就原样返回 —— 搜索框空着不该改变列表 */
export function searchTasks<T extends SearchableTask>(tasks: readonly T[], keyword: string): T[] {
  if (keyword.trim() === '') return [...tasks];
  return tasks.filter((t) => taskMatches(t, keyword));
}
